<template>
  <div class="container-fluid">
    <div class="page-title">
      <div class="row">
        <div class="col-6">
          <h3>Editar viaje</h3>
        </div>
        <div class="col-6 text-end">
          <button class="btn btn-outline-secondary" @click="$router.replace('/trips/consulta')">
            Regresar
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <div v-else-if="errorMsg" class="alert alert-danger" role="alert">
      {{ errorMsg }}
    </div>

    <div v-else>
      <div class="alert alert-warning" v-if="!canModifyTrip">
        No tienes permisos para modificar este viaje.
      </div>

      <div class="card mt-3">
        <div class="card-header pb-0">
          <h4 class="mb-0">Paso {{ step }} de 3</h4>
          <span class="text-muted small">
            {{ step === 1 ? 'Selecciona origen' : step === 2 ? 'Selecciona destino' : 'Confirma datos' }}
          </span>
        </div>
        <div class="card-body">
          <div v-if="step === 1">
            <MapLocationPicker
              label="Origen"
              subtitle="Busca una dirección o selecciona en el mapa"
              :initialLatitude="origin.latitude"
              :initialLongitude="origin.longitude"
              :initialAddress="origin.address"
              @confirm="onConfirmOrigin"
            />
          </div>

          <div v-else-if="step === 2">
            <div class="mb-3">
              <h5 class="mb-1">Origen confirmado</h5>
              <div class="text-muted small">
                {{ origin.address || '-' }}
                <span v-if="origin.latitude && origin.longitude">
                  ({{ formatCoord(origin.latitude) }}, {{ formatCoord(origin.longitude) }})
                </span>
              </div>
            </div>

            <MapLocationPicker
              label="Destino"
              subtitle="Busca una dirección o selecciona en el mapa"
              :initialLatitude="destination.latitude"
              :initialLongitude="destination.longitude"
              :initialAddress="destination.address"
              @confirm="onConfirmDestination"
            />
          </div>

          <div v-else>
            <div class="row g-3">
              <div class="col-lg-6">
                <div class="p-3 border rounded">
                  <h5 class="mb-1">Origen</h5>
                  <div class="text-muted small">{{ origin.address || '-' }}</div>
                  <div v-if="origin.latitude && origin.longitude" class="small">
                    {{ formatCoord(origin.latitude) }}, {{ formatCoord(origin.longitude) }}
                  </div>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="p-3 border rounded">
                  <h5 class="mb-1">Destino</h5>
                  <div class="text-muted small">{{ destination.address || '-' }}</div>
                  <div v-if="destination.latitude && destination.longitude" class="small">
                    {{ formatCoord(destination.latitude) }}, {{ formatCoord(destination.longitude) }}
                  </div>
                </div>
              </div>

              <div class="col-12">
                <h5 class="mt-2 mb-2">Fecha y horario</h5>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Fecha del viaje</label>
                    <input v-model="form.fechaViaje" type="date" class="form-control" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Hora del viaje</label>
                    <input v-model="form.horaViaje" type="time" class="form-control" />
                  </div>
                </div>
              </div>

              <div class="col-12 d-flex justify-content-between">
                <button class="btn btn-outline-secondary" :disabled="loading" @click="step = 2">
                  Atrás
                </button>
                <button
                  class="btn btn-primary"
                  :disabled="loadingSave || !canModifyTrip"
                  @click="submit"
                >
                  {{ loadingSave ? 'Guardando...' : 'Guardar cambios' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-3 d-flex justify-content-between">
        <button class="btn btn-outline-secondary" :disabled="step === 1 || loadingSave" @click="step--">
          Anterior
        </button>
        <button
          v-if="step === 1 && origin.address"
          class="btn btn-success"
          :disabled="loadingSave || origin.latitude === null || origin.longitude === null"
          @click="step = 2"
        >
          Continuar
        </button>
        <button
          v-if="step === 2 && destination.address"
          class="btn btn-success"
          :disabled="loadingSave || destination.latitude === null || destination.longitude === null"
          @click="step = 3"
        >
          Continuar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { tripsApi } from '@/api/trips';
import MapLocationPicker from '@/components/trips/MapLocationPicker.vue';

export default {
  name: 'EditarViaje',
  components: {
    MapLocationPicker,
  },
  data() {
    return {
      loading: false,
      loadingSave: false,
      errorMsg: '',
      step: 1,
      tripId: null,
      tripData: null,
      origin: { latitude: null, longitude: null, address: '' },
      destination: { latitude: null, longitude: null, address: '' },
      form: {
        fechaViaje: '',
        horaViaje: '',
      },
    };
  },
  computed: {
    currentRole() {
      return this.$store.getters['auth/role'];
    },
    currentUserId() {
      return this.$store.getters['auth/user']?.id;
    },
    canModifyTrip() {
      if (!this.tripData) return true;
      if (this.currentRole === 'ADMINISTRADOR') return true;
      return this.currentRole === 'CONDUCTOR_VIAJERO' && this.tripData.travelerId === this.currentUserId;
    },
  },
  mounted() {
    this.tripId = this.$route.params.id;
    this.loadTrip();
  },
  methods: {
    formatCoord(v) {
      if (!Number.isFinite(v)) return '-';
      return Number(v).toFixed(6);
    },
    pad(n) {
      return String(n).padStart(2, '0');
    },
    toDateInputValue(date) {
      const d = new Date(date);
      return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
    },
    toTimeInputValue(date) {
      const d = new Date(date);
      return `${this.pad(d.getHours())}:${this.pad(d.getMinutes())}`;
    },
    onConfirmOrigin(loc) {
      this.origin = { ...this.origin, ...loc, address: loc.address || this.origin.address };
      this.step = 2;
    },
    onConfirmDestination(loc) {
      this.destination = { ...this.destination, ...loc, address: loc.address || this.destination.address };
      this.step = 3;
    },
    async loadTrip() {
      this.loading = true;
      this.errorMsg = '';
      try {
        const { data } = await tripsApi.getById(this.tripId);
        const trip = data || {};
        this.origin = {
          latitude: trip.originLatitude,
          longitude: trip.originLongitude,
          address: trip.originAddress || '',
        };
        this.destination = {
          latitude: trip.destinationLatitude,
          longitude: trip.destinationLongitude,
          address: trip.destinationAddress || '',
        };
        this.form.fechaViaje = this.toDateInputValue(trip.tripDate);
        this.form.horaViaje = this.toTimeInputValue(trip.tripTime);

        this.tripData = trip;

        // Validación de permisos (via frontend)
        const can =
          this.currentRole === 'ADMINISTRADOR' || (this.currentRole === 'CONDUCTOR_VIAJERO' && trip.travelerId === this.currentUserId);
        if (!can) {
          await Swal.fire({
            icon: 'error',
            title: 'Sin permisos',
            text: 'No puedes modificar este viaje.',
            confirmButtonColor: '#0d6efd',
          });
          this.$router.replace('/trips/consulta');
        }
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'Error al cargar el viaje.';
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      this.loadingSave = true;
      try {
        if (!this.tripId) throw new Error('ID de viaje inválido');
        if (!this.origin.latitude || !this.origin.longitude || !this.origin.address) {
          throw new Error('Confirma el origen antes de guardar.');
        }
        if (this.destination.latitude === null || this.destination.longitude === null || !this.destination.address) {
          throw new Error('Confirma el destino antes de guardar.');
        }
        if (!this.form.fechaViaje || !this.form.horaViaje) {
          throw new Error('Completa la fecha y hora del viaje.');
        }

        const fecha = this.form.fechaViaje;
        const hora = this.form.horaViaje;
        const fechaViajeISO = new Date(`${fecha}T00:00:00`).toISOString();
        const horaViajeISO = new Date(`${fecha}T${hora}`).toISOString();
        const payload = {
          fechaViaje: fechaViajeISO,
          horaViaje: horaViajeISO,
          origen: {
            latitude: this.origin.latitude,
            longitude: this.origin.longitude,
            direccion: this.origin.address,
          },
          destino: {
            latitude: this.destination.latitude,
            longitude: this.destination.longitude,
            direccion: this.destination.address,
          },
        };

        await tripsApi.update(this.tripId, payload);

        await Swal.fire({
          icon: 'success',
          title: 'Cambios guardados',
          text: 'El viaje se actualizó correctamente.',
          confirmButtonColor: '#0d6efd',
        });

        this.$router.replace('/trips/consulta');
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'Error al guardar cambios.';
        await Swal.fire({
          icon: 'error',
          title: 'No se pudo guardar',
          text: Array.isArray(msg) ? msg.join(' ') : msg,
          confirmButtonColor: '#0d6efd',
        });
      } finally {
        this.loadingSave = false;
      }
    },
  },
};
</script>

<style scoped>
.mapbox-container {
  border-radius: 8px;
  overflow: hidden;
}
</style>

